'use client'

import { useState, useEffect } from 'react'
import {
  Activity,
  X,
  ChevronRight,
  Loader2,
  Square,
  RotateCcw,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  ListTodo,
  BarChart3,
  Layers,
  Box,
  Terminal,
  GitBranch,
  Image,
  Cpu,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTaskSystem } from '@/lib/hooks/use-task-system'
import { commandApi } from '@/lib/api/command'
import { pipelineApi } from '@/lib/api/pipeline'
import { creationApi } from '@/lib/api/creation'
import { useFakeProgress } from '@/lib/hooks/use-fake-progress'
import type { PoolInfo } from '@/lib/api/task-monitor'
import type { PipelineProgressStage } from '@/lib/types/pipeline'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { toast } from 'sonner'
import { useI18n } from '@/components/providers/I18nProvider'

const ACTIVE_PIPELINE_STATUSES = ['pending', 'running']
const ACTIVE_TASK_STATUSES = ['pending', 'queued', 'running']

function pipelineTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    ecommerce_main_image: '电商主图',
    ecommerce_detail_image: 'A+详情图',
    ecommerce_hit_replicate: '爆款复刻',
    ecommerce_clothing_set: '服装组图',
    ecommerce_model_wear: '模特穿戴',
    ecommerce_product_refine: '产品精修',
    ecommerce_video: '电商视频',
  }
  return labels[type] || type
}

function getStepLabel(step: string, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const known: Record<string, string> = {
    analyze_product: 'pipeline.step.analyze_product',
    generate_images: 'pipeline.step.generate_images',
    generate_video: 'pipeline.step.generate_video',
    collect_results: 'pipeline.step.collect_results',
    start: 'pipeline.step.start',
  }
  const key = known[step]
  if (key) return t(key)
  if (step.startsWith('generate_')) {
    const n = step.replace('generate_', '').replace('_', ' ')
    return t('pipeline.step.generate', { n })
  }
  return step
}

// ── Status helpers ────────────────────────────────────────────────────

function statusColor(status: string): string {
  switch (status) {
    case 'running': return 'text-blue-500'
    case 'pending': case 'queued': case 'new': return 'text-amber-500'
    case 'succeeded': case 'completed': return 'text-emerald-500'
    case 'failed': return 'text-red-500'
    case 'canceled': return 'text-gray-400'
    case 'partially_succeeded': return 'text-yellow-500'
    default: return 'text-muted-foreground'
  }
}

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'running': return 'default'
    case 'pending': case 'queued': case 'new': return 'secondary'
    case 'succeeded': case 'completed': return 'default'
    case 'failed': return 'destructive'
    case 'canceled': return 'outline'
    default: return 'outline'
  }
}

function statusIcon(status: string) {
  switch (status) {
    case 'running': return <Loader2 className="h-3 w-3 animate-spin" />
    case 'pending': case 'queued': case 'new': return <Clock className="h-3 w-3" />
    case 'succeeded': case 'completed': return <CheckCircle2 className="h-3 w-3" />
    case 'failed': return <AlertCircle className="h-3 w-3" />
    case 'canceled': return <X className="h-3 w-3" />
    default: return <Activity className="h-3 w-3" />
  }
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: '等待中', queued: '排队中', running: '运行中',
    succeeded: '已完成', failed: '失败', canceled: '已取消',
    completed: '已完成', new: '新的', partially_succeeded: '部分成功',
  }
  return labels[status] || status
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const diff = Date.now() - d.getTime()
  if (diff < 60000) return '刚刚'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
  return d.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// ── Real-time elapsed time ────────────────────────────────────────────
// Called on every render. The parent component re-renders every 1s via
// the `tick` state, so elapsed time stays live without a dedicated hook.
function elapsedTimeLive(startedAt: string | null | undefined): string {
  if (!startedAt) return '—'
  const diff = Date.now() - new Date(startedAt).getTime()
  const mins = Math.floor(diff / 60000)
  const secs = Math.floor((diff % 60000) / 1000)
  if (mins > 0) return `${mins}分${secs}秒`
  return `${secs}秒`
}

/** Format duration between two ISO timestamps, or from start to now if end is omitted. */
function formatDuration(startedAt: string | null | undefined, endedAt?: string | null | undefined): string {
  if (!startedAt) return '—'
  const end = endedAt ? new Date(endedAt).getTime() : Date.now()
  const diff = end - new Date(startedAt).getTime()
  if (diff < 0) return '—'
  const mins = Math.floor(diff / 60000)
  const secs = Math.floor((diff % 60000) / 1000)
  if (mins > 0) return `${mins}分${secs}秒`
  return `${secs}秒`
}

// ── Pool bar component ────────────────────────────────────────────────

function PoolBar({ label, pool, icon: Icon }: { label: string; pool: PoolInfo; icon: React.ComponentType<{ className?: string }> }) {
  const pct = pool.max > 0 ? (pool.active / pool.max) * 100 : 0
  const isFull = pool.active >= pool.max
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="text-[11px] w-10 shrink-0 text-muted-foreground">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500',
            isFull ? 'bg-red-500' : pool.active > 0 ? 'bg-primary' : 'bg-muted-foreground/20',
          )}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="text-[11px] tabular-nums w-12 text-right shrink-0 text-muted-foreground">
        {pool.active}/{pool.max}
      </span>
    </div>
  )
}

// ── Floating Button ───────────────────────────────────────────────────

export function TaskMonitorFloatingButton() {
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  // 1-second tick so elapsed time updates in real-time
  const [tick, setTick] = useState(0)
  const { t } = useI18n()

  // ONE shared hook — single source of truth
  const { data, isLoading, refetch } = useTaskSystem()

  // Force re-render every second while the panel is open so elapsed time stays live
  useEffect(() => {
    if (!isOpen) return
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [isOpen])

  const pipelines = data?.pipelines?.items ?? []
  const creationTasks = data?.creation_tasks?.items ?? []
  const activePipelines = pipelines.filter(p => ACTIVE_PIPELINE_STATUSES.includes(p.status))
  const activeCreationTasks = creationTasks.filter(t => ACTIVE_TASK_STATUSES.includes(t.status))

  // ── Actions ────────────────────────────────────────────────

  const handleCancelPipeline = async (id: string) => {
    try { await pipelineApi.cancelPipeline(id); toast.success('已取消流水线'); refetch() }
    catch { toast.error('取消失败') }
  }

  const handleDeletePipeline = async (id: string) => {
    try { await pipelineApi.deletePipeline(id); toast.success('已删除流水线'); refetch() }
    catch { toast.error('删除失败') }
  }

  const handleRetryPipeline = async (id: string) => {
    try { await pipelineApi.retryPipeline(id); toast.success('已重试流水线'); refetch() }
    catch { toast.error('重试失败') }
  }

  const handleCancelTask = async (id: string) => {
    try { await creationApi.cancelTask(id); toast.success('已取消任务'); refetch() }
    catch { toast.error('取消失败') }
  }

  const handleDeleteTask = async (id: string) => {
    try { await creationApi.deleteTask(id); toast.success('已删除任务'); refetch() }
    catch { toast.error('删除失败') }
  }

  const handleCancelCommand = async (id: string) => {
    try { await commandApi.cancelJob(id); toast.success('已取消命令'); refetch() }
    catch { toast.error('取消失败') }
  }

  // ── Badge count ────────────────────────────────────────────

  const activeCount = activePipelines.length + activeCreationTasks.length + (data?.queue.by_status?.running || 0)

  return (
    <>
      <button
        data-task-monitor
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen) }}
        className={cn(
          'fixed bottom-6 right-6 z-40 flex items-center justify-center gap-2 rounded-full p-3 shadow-lg transition-all duration-200',
          isOpen
            ? 'bg-primary text-primary-foreground shadow-xl scale-105'
            : 'bg-background border border-border hover:shadow-xl hover:scale-105',
        )}
        style={{ cursor: 'pointer' }}
      >
        {isOpen ? (
          <X className="h-5 w-5" />
        ) : (
          <>
            <Activity className="h-5 w-5 text-primary" />
            {activeCount > 0 && (
              <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">
                {activeCount > 99 ? '99+' : activeCount}
              </span>
            )}
          </>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setIsOpen(false)} />
          <div data-task-monitor className={cn(
            'fixed bottom-24 right-6 z-50 w-[480px] max-h-[70vh] rounded-xl border bg-background shadow-2xl overflow-hidden',
            'flex flex-col',
          )}>
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-semibold">任务监控</h2>
                {data && (
                  <span className="text-[11px] text-muted-foreground">
                    {data.queue.worker.active}/{data.queue.worker.max_tasks} 运行中
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => refetch()} title="刷新">
                  <Loader2 className={cn('h-3 w-3', isLoading && 'animate-spin')} />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsOpen(false)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Loading state */}
            {isLoading && !data && (
              <div className="flex flex-col items-center justify-center py-16 gap-2">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">加载任务状态...</p>
              </div>
            )}

            {/* Empty state */}
            {!isLoading && data && pipelines.length === 0 && creationTasks.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
                <Activity className="h-8 w-8 opacity-30" />
                <p className="text-xs">暂无任务</p>
              </div>
            )}

            {/* Content */}
            {data && (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                <div className="px-4 pt-2 shrink-0">
                  <TabsList className="w-full h-8">
                    <TabsTrigger value="overview" className="text-xs h-7 gap-1">
                      <BarChart3 className="h-3 w-3" />概览
                    </TabsTrigger>
                    <TabsTrigger value="pipelines" className="text-xs h-7 gap-1 relative">
                      <Layers className="h-3 w-3" />流水线
                      {activePipelines.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activePipelines.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="tasks" className="text-xs h-7 gap-1 relative">
                      <Box className="h-3 w-3" />生图任务
                      {activeCreationTasks.length > 0 && (
                        <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activeCreationTasks.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="commands" className="text-xs h-7 gap-1">
                      <Terminal className="h-3 w-3" />队列
                    </TabsTrigger>
                  </TabsList>
                </div>

                <ScrollArea className="flex-1 px-4 py-3">
                  <TabsContent value="overview" className="mt-0 space-y-4">
                    {/* Pool concurrency bars */}
                    {data.queue.worker.pools && (
                      <div className="rounded-lg border p-3 space-y-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-muted-foreground">并发池占用</span>
                          <span className="text-[11px] text-muted-foreground">
                            排队命令: {data.queue.by_status?.new || 0}
                          </span>
                        </div>
                        <PoolBar label="流水线" pool={data.queue.worker.pools.pipeline} icon={GitBranch} />
                        <PoolBar label="生图" pool={data.queue.worker.pools.creation} icon={Image} />
                        <PoolBar label="其他" pool={data.queue.worker.pools.other} icon={Cpu} />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <SummaryCard label="流水线" active={activePipelines.length} total={pipelines.length} icon={Layers} />
                      <SummaryCard label="生图任务" active={activeCreationTasks.length} total={creationTasks.length} icon={Box} />
                      <SummaryCard label="队列命令" active={data.queue.by_status?.running || 0} total={data.queue.total} icon={ListTodo} />
                      <SummaryCard label="已完成" active={data.queue.by_status?.completed || 0} total={0} icon={CheckCircle2} color="text-emerald-500" />
                    </div>

                    {activePipelines.length > 0 && (
                      <>
                        <h3 className="text-xs font-medium text-muted-foreground">运行中的流水线</h3>
                        <div className="space-y-1.5">
                          {activePipelines.slice(0, 5).map((p) => (
                            <MiniTaskRow
                              key={p.id}
                              title={p.title || pipelineTypeLabel(p.pipeline_type)}
                              status={p.status}
                              progress={p.progress}
                              time={elapsedTimeLive(p.started_at)}
                              onClick={() => setActiveTab('pipelines')}
                              stepStartedAt={p.step_started_at}
                              progressConfig={p.progress_config}
                              currentStep={p.current_step ? getStepLabel(p.current_step, t) : undefined}
                              totalSteps={p.total_steps}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {activeCreationTasks.length > 0 && (
                      <>
                        <h3 className="text-xs font-medium text-muted-foreground">运行中的生图任务</h3>
                        <div className="space-y-1.5">
                          {activeCreationTasks.slice(0, 5).map((t) => (
                            <MiniTaskRow
                              key={t.id}
                              title={t.title || t.creation_type}
                              status={t.status}
                              progress={t.progress}
                              subtitle={`${t.model_name || t.provider}`}
                              time={elapsedTimeLive(t.started_at)}
                              onClick={() => setActiveTab('tasks')}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="pipelines" className="mt-0 space-y-2">
                    {activePipelines.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                        <Layers className="h-8 w-8 opacity-30" /><p className="text-xs">暂无运行中的流水线</p>
                      </div>
                    )}
                    {activePipelines.map((p) => (
                      <TaskRow
                        key={p.id}
                        title={p.title || pipelineTypeLabel(p.pipeline_type)}
                        status={p.status}
                        progress={p.progress}
                        subtitle={pipelineTypeLabel(p.pipeline_type)}
                        time={formatTime(p.created_at)}
                        error={p.error_message}
                        duration={['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(p.status)
                          ? formatDuration(p.started_at, p.updated_at)
                          : elapsedTimeLive(p.started_at)}
                        totalSteps={p.total_steps}
                        currentStep={p.current_step ? getStepLabel(p.current_step, t) : undefined}
                        stepStartedAt={p.step_started_at}
                        progressConfig={p.progress_config}
                        onCancel={() => handleCancelPipeline(p.id)}
                        onDelete={() => handleDeletePipeline(p.id)}
                        onRetry={p.status === 'failed' ? () => handleRetryPipeline(p.id) : undefined}
                      />
                    ))}
                  </TabsContent>

                  <TabsContent value="tasks" className="mt-0 space-y-2">
                    {activeCreationTasks.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                        <Box className="h-8 w-8 opacity-30" /><p className="text-xs">暂无运行中的生图任务</p>
                      </div>
                    )}
                    {activeCreationTasks.map((t) => (
                      <TaskRow
                        key={t.id}
                        title={t.title || t.creation_type}
                        status={t.status}
                        progress={t.progress}
                        subtitle={`${t.model_name || t.provider} · ${t.module_type}`}
                        time={formatTime(t.created_at)}
                        error={t.error_message}
                        duration={['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(t.status)
                          ? formatDuration(t.started_at, t.updated_at)
                          : elapsedTimeLive(t.started_at)}
                        progressLabel={`${t.succeeded_output_count || 0}/${t.expected_output_count || 1}`}
                        onCancel={() => handleCancelTask(t.id)}
                        onDelete={() => handleDeleteTask(t.id)}
                      />
                    ))}
                  </TabsContent>

                  <TabsContent value="commands" className="mt-0 space-y-2">
                    {data.recent_commands && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {Object.entries(data.queue.by_status).map(([s, count]) => (
                          <Badge key={s} variant={statusBadgeVariant(s)} className="flex items-center gap-1">
                            {statusIcon(s)}{statusLabel(s)}<span className="font-bold">{count}</span>
                          </Badge>
                        ))}
                      </div>
                    )}
                    {(!data.recent_commands || data.recent_commands.length === 0) && (
                      <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                        <Terminal className="h-8 w-8 opacity-30" /><p className="text-xs">暂无命令记录</p>
                      </div>
                    )}
                    {data.recent_commands?.map((cmd) => (
                      <TaskRow
                        key={cmd.id}
                        title={cmd.name}
                        status={cmd.status}
                        progress={cmd.attempts > 0 ? Math.min(cmd.attempts * 20, 100) : undefined}
                        subtitle={cmd.app}
                        time={formatTime(cmd.created_at)}
                        error={cmd.error_message}
                        duration={['succeeded', 'completed', 'failed', 'canceled'].includes(cmd.status)
                          ? formatDuration(cmd.started_at, cmd.completed_at ?? cmd.updated_at)
                          : elapsedTimeLive(cmd.started_at)}
                        onAction={cmd.status === 'running' ? () => handleCancelCommand(cmd.id) : undefined}
                      />
                    ))}
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            )}
          </div>
        </>
      )}
    </>
  )
}

// ── Sub-components ────────────────────────────────────────────────────

function SummaryCard({ label, active, total, icon: Icon, color }: {
  label: string; active: number; total: number; icon: React.ComponentType<{ className?: string }>; color?: string
}) {
  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-center justify-between mb-1">
        <Icon className={cn('h-3.5 w-3.5', color || 'text-muted-foreground')} />
        {active > 0 && (
          <span className="text-[10px] font-bold text-amber-500 bg-amber-50 dark:bg-amber-950/30 px-1.5 py-0.5 rounded">{active} 活跃</span>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-bold">{total}</span>
        <span className="text-[11px] text-muted-foreground">{label}</span>
      </div>
    </div>
  )
}

function MiniTaskRow({ title, status, progress, time, subtitle, onClick, stepStartedAt, progressConfig, currentStep, totalSteps }: {
  title: string; status: string; progress?: number; time: string; subtitle?: string; onClick?: () => void
  stepStartedAt?: string | null; progressConfig?: PipelineProgressStage[]
  currentStep?: string | null; totalSteps?: number | null
}) {
  const isActive = ['running', 'pending', 'queued', 'new'].includes(status)
  const isDone = ['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(status)
  const displayProgress = useFakeProgress(progress ?? 0, isActive, isDone, stepStartedAt, progressConfig)

  return (
    <button onClick={onClick} className="w-full flex items-center gap-2 rounded-lg border px-3 py-2 hover:bg-accent/50 transition-colors text-left">
      <div className={cn('shrink-0', statusColor(status))}>{statusIcon(status)}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium truncate">{title}</span>
          {subtitle && <span className="text-[10px] text-muted-foreground truncate">{subtitle}</span>}
        </div>
        {currentStep && isActive && (
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[10px] text-blue-500 truncate">{currentStep}</span>
            {totalSteps && <span className="text-[10px] text-muted-foreground">/ {totalSteps}步</span>}
          </div>
        )}
        {progress !== undefined && progress > 0 && (
          <div className="h-1 rounded-full bg-muted mt-1 overflow-hidden max-w-[120px]">
            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(displayProgress, 100)}%` }} />
          </div>
        )}
      </div>
      <span className="text-[10px] text-muted-foreground shrink-0">{time}</span>
      <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
    </button>
  )
}

function TaskRow({ title, status, progress, subtitle, time, error, duration, progressLabel, totalSteps, currentStep, stepStartedAt, progressConfig, onCancel, onDelete, onRetry, onAction }: {
  title: string; status: string; progress?: number; subtitle?: string; time: string; error?: string | null; duration?: string
  progressLabel?: string; totalSteps?: number | null; currentStep?: string | null; stepStartedAt?: string | null; progressConfig?: PipelineProgressStage[]
  onCancel?: () => void; onDelete?: () => void; onRetry?: () => void; onAction?: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const isActive = ['running', 'pending', 'queued', 'new'].includes(status)
  const isDone = ['succeeded', 'completed', 'failed', 'canceled', 'partially_succeeded'].includes(status)
  const displayProgress = useFakeProgress(progress ?? 0, isActive, isDone, stepStartedAt, progressConfig)

  return (
    <div className="rounded-lg border overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-accent/50 transition-colors text-left">
        <div className={cn('shrink-0', statusColor(status))}>{statusIcon(status)}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium truncate">{title}</span>
            <Badge variant={statusBadgeVariant(status)} className="h-5 px-1.5 text-[10px]">{statusLabel(status)}</Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {subtitle && <span className="text-[10px] text-muted-foreground truncate">{subtitle}</span>}
            {duration && duration !== '—' && <span className="text-[10px] text-muted-foreground">{duration}</span>}
            {progressLabel && <span className="text-[10px] text-muted-foreground">输出: {progressLabel}</span>}
          </div>
          {currentStep && isActive && (
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-[10px] text-blue-500 font-medium">当前: {currentStep}</span>
              {totalSteps && <span className="text-[10px] text-muted-foreground">/ 共{totalSteps}步</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {progress !== undefined && progress > 0 && <span className="text-[11px] font-medium tabular-nums text-muted-foreground min-w-[32px] text-center">{displayProgress}%</span>}
          <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', expanded && 'rotate-90')} />
        </div>
      </button>

      {progress !== undefined && progress > 0 && (
        <div className="h-1 bg-muted">
          <div className={cn('h-full', status === 'failed' ? 'bg-red-500' : 'bg-primary')} style={{ width: `${Math.min(displayProgress, 100)}%` }} />
        </div>
      )}

      {expanded && (
        <div className="border-t px-3 py-2 space-y-2 bg-muted/30">
          {currentStep && (
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">当前步骤</span>
              <span className="font-medium">{currentStep}</span>
            </div>
          )}
          {totalSteps && (
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">总步骤</span>
              <span className="font-medium">{totalSteps}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-muted-foreground">创建时间</span>
            <span className="font-medium">{time}</span>
          </div>
          {duration && duration !== '—' && (
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">耗时</span>
              <span className="font-medium">{duration}</span>
            </div>
          )}

          {error && (
            <div className="rounded bg-red-50 dark:bg-red-950/20 p-2 text-[11px] text-red-600 dark:text-red-400 break-words">{error}</div>
          )}

          <div className="flex items-center gap-1 pt-1 border-t">
            {isActive && onCancel && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                onClick={(e) => { e.stopPropagation(); onCancel() }}>
                <Square className="h-3 w-3" />取消
              </Button>
            )}
            {onRetry && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1"
                onClick={(e) => { e.stopPropagation(); onRetry() }}>
                <RotateCcw className="h-3 w-3" />重试
              </Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={(e) => { e.stopPropagation(); onDelete() }}>
                <Trash2 className="h-3 w-3" />删除
              </Button>
            )}
            {onAction && (
              <Button variant="ghost" size="sm" className="h-7 text-[11px] gap-1"
                onClick={(e) => { e.stopPropagation(); onAction() }}>
                <Square className="h-3 w-3" />取消
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}